import router from "@/router";
import { defineStore } from "pinia";
import { getResponseError } from "@/helpers/api";
import { useAlertStore } from "@/stores/alert";
import { usePwaStore } from "./pwa";
import { useGlobalStateStore } from "@/stores/global";
import AuthService from "@/services/AuthService";
import type { AuthStore } from "@/types/stores";
import axios, { AxiosError } from "axios";
import Bugsnag from "@bugsnag/js";
import { Modal } from "bootstrap";

export const useAuthStore = defineStore("auth", {
	state: (): AuthStore => {
		return {
			user: null,
			error: null,
			loading: false
		};
	},
	actions: {
		/**
		 * Work out the user's authentication state
		 * @returns {Promise<boolean>} True if logged in on the server or false
		 * if not.
		 */
		async state (): Promise<boolean> {
			const authService = new AuthService();
			try {
				await authService.state();
			} catch (error: unknown) {
				if (axios.isAxiosError(error)) {
					const axError = error as AxiosError;
					if (axError.response?.status === 401) {
						return false;
					} else {
						throw error;
					}
				}
			}
			return true;
		},
		async login (payload: any, noForward = false) {
			const authStore = useAuthStore();
			const authService = new AuthService();
			const alertStore = useAlertStore();
			const globalStateStore = useGlobalStateStore();
			const pwaStore = usePwaStore();
			// ensure app is inituilly set to manually online or we cant actually log in
			pwaStore.setManuallyOnline(true);
			try {
				// Ensure the CSRF cookie is up to date before logging in
				await authService.getCsrfCookie();
				// log the user in (does not retrieve the user, do not use the response to set the user)
				const response = await authService.login(payload);
				// console.log("login response", response);
				if (authStore.user != null) {
					console.log("User already loaded, requesting updates.."
					);
					pwaStore.requireSync = true;
				}
				else {
					alertStore.clearAll();
					await this.getCurrentUser();
					if (!this.user.email_verified_at) alertStore.info("Check your email to verify your email address. You can resend the verification email from your profile");
					if (!this.user.password_updated_at) alertStore.info("Please <a href=\"/panel/profile\">update your password</a> to meet new security standards.");
				}
				if (!noForward) await router.push("/panel/dashboard");
			} catch (error:any) {
				if (!authStore.user) {
					if (error.response) {
						// The request was made and the server responded with a status code
						// that falls out of the range of 2xx
						console.error("Server responded with an error:", error.response.data);

						// Assuming Laravel returns an error message in the response
						this.error = error.response.data.message;
					} else if (error.request) {
						// The request was made but no response was received
						console.error("No response received from the server:", error.request);
					} else {
						// Something happened in setting up the request that triggered an Error
						// @TODO: seems to be landing here for incorrect credentials when it should be catching the 422 (unprocessable content error)
						// Update: This is now fixed in resources/js/plugins/axios.ts so the 422 is now being handled
						// This code can probably be removed now?
						console.error("An error occurred while setting up the request:", error.message);
						alertStore.error("User credentials don't match, please re-enter your username and password.");
						return;
					}
					alertStore.error(getResponseError(error));
				}
				else console.log(getResponseError(error));
			}
		},
		async reauthenticateUser (password: string) {
			const globalStateStore = useGlobalStateStore();
			const alertStore = useAlertStore();
			const authStore = useAuthStore();
			const pwaStore = usePwaStore();
			const authService = new AuthService();
			await authService.getCsrfCookie();
			try {
				const response = await authService.login({
					email: authStore.user.email,
					password: password
				});
				// console.log("login response", response);
				// const res = await authService.reauthenticateUser({
				// 	email: authStore.user.email,
				// 	password: password
				// });
				alertStore.clearString("Invalid password");
				// require a full sync because we dont know what happened while the user was offline
				// (patch user data then get user)
				pwaStore.requireSync = true;

				return true;
			} catch (e) {
				console.log(e);
				alertStore.error("Invalid password");
				return false;
			}

		},

		async logoutOtherSessions (password: string) {
			const alertStore = useAlertStore();
			const authService = new AuthService();
			authService.logoutOtherSessions(password);
			alertStore.clearString("Are you using more than one device simultaneously? This can cause data loss. Click on your profile icon to log out all other devices aside from this one.");
			return true;
		},

		async register (payload: any) {
			const authService = new AuthService();
			const alertStore = useAlertStore();
			// Ensure the CSRF cookie is up to date before logging in
			await authService.getCsrfCookie();
			try {
				const response = await authService.registerUser(payload);
				// At this point the user does not have a session but can login
				// so redirect them to the login screen with a success message
				console.log("Please check your email and follow the link to verify your email address");
				alertStore.success("Please check your email and follow the link to verify your email address.");
				await router.push("/panel/dashboard");
			} catch (error) {
				alertStore.error(getResponseError(error));
			}
		},

		async getCurrentUser () {
			// if (this.user == null) return null;

			this.loading = true;
			const authService = new AuthService();
			const alertStore = useAlertStore();
			const globalStateStore = useGlobalStateStore();

			// confirm session first
			// if the session isnt valid, clear alerts and trigger reauthentication modal
			if (!this.checkSession()) {
				return;
			}
			try {
				const response = await authService.getCurrentUser();
				this.user = response.data.data;
			} catch (error) {
				// this.user = null;
				await authService.logout();
				this.error = getResponseError(error);
			}
			this.loading = false;
			return this.user;

			// this.loading = true;
			// const authService = new AuthService();
			// const alertStore = useAlertStore();
			// // if (this.user == null) return this.user;
			// try {
			// 	const response = await authService.getCurrentUser();
			// 	this.user = response.data.data;
			// 	if (this.user.type == "owner")
			// 		if (this.user.clients.length == 0) {
			// 			const result : any[] = [];

			// 			const client = {
			// 				id: uuidv4(),
			// 				estates: result,
			// 				name: this.user.full_name,
			// 				created_at: new Date(),
			// 				updated_at: new Date(),
			// 				user_id: this.user.id
			// 			};
			// 			const estate = {
			// 				id: uuidv4(),
			// 				created_at: new Date(),
			// 				sites: [],
			// 				name: this.user.full_name,
			// 				updated_at: new Date(),
			// 				client_id: client.id };
			// 			client.estates.push(estate);
			// 			this.user.clients.push(client);
			// 		}
			// 	this.loading = false;
			// } catch (error) {
			// 	this.loading = false;
			// 	this.user = null;
			// 	this.error = getResponseError(error);
			// 	alertStore.error(getResponseError(error));
			// }
			// return this.user;
		},

		async checkSession () {
			const globalStateStore = useGlobalStateStore();
			const alertStore = useAlertStore();
			try {
				const response = await axios.get("/session/check");
				globalStateStore.sessionExpired = false;
				return true;
			} catch (error: any) {
				// if the session isnt valid, clear alerts and trigger reauthentication modal
				console.log("Session invalid:", error.response?.status);
				alertStore.clearAll();
				globalStateStore.sessionExpired = true;
				return false;
			}
		},

		// PATCH user in the DB then GET user from the db
		async syncCurrentUser (force?: boolean): Promise<void> {
			// abort is the user is not valid in the FE and send to log in
			if (this.user == null) {
				console.log("couldn't sync, user is null");
				await router.push("/login");
				throw new Error("syncCurrentUser called, but this.user is null - redirected to login.");
			}

			const pwaStore = usePwaStore();
			// abort if the user has manually set their device to offline or  is offline for any reason
			if (pwaStore.updateInProgress ||
				(!pwaStore.onlineAndConnected) ||
				pwaStore.manuallyOffline()) {
				console.log("couldn't sync, user is offline");
				return;
			}

			console.log("syncing current user...");
			this.loading = true;

			// TODO: check session before continuing
			// Ensure the session is active before proceeding. It might have
			// timed out in 2 cases:
			// 1. while the user was offline, so we want to renew it now they are
			// coming online.
			// 2. while the user was online, which threw an error in the API layer
			// and triggered a sync to recover. Again, we want to renew it to ensure
			// we recover successfully,

			pwaStore.startUpdate();

			try {
				// check if we are forcing an update even if the server data has been edited more recently
				// (ie we have an older synced_at timestamp for the FE data). Only
				// send force_mobile when the caller explicitly passed force -
				// omitting it (the common, no-argument case, eg the periodic
				// auto-sync poll) lets the backend fall back to its own
				// mobile/tablet user-agent detection instead of us forcing it off.
				const params: { full: boolean; force_mobile?: boolean } = { full: true };
				if (force !== undefined) {
					params.force_mobile = force;
				}
				await axios.patch(route("api.user.update", { id: this.user.id }), this.user, { params })
					.catch((error) => {
						console.log("error:", error);
					});
				// success but will overwrite some data in the UI:

				this.loading = false;
				pwaStore.requireSync = false;
			} catch (error) {
				console.error("syncCurrentUser failed:", error);
				this.loading = false;
				// why was this false?! v
				this.error = getResponseError(error);
				// We need to handle this error very carefully, because syncCurrentUser
				// is responsible both for syncing offline changes AND recovering after
				// an error elsewhere. So if we're here, it has failed and we are in
				// trouble! We need to inform the user and block the UI to stop them
				// doing more work, which can be done with a modal.
				const config = this.error.config || {};
				const requestData = config.data ? JSON.parse(config.data) : null;
				if (Bugsnag.isStarted()) {
					Bugsnag.notify(error as Error);
					Bugsnag.notify(error as Error, event => {
						event.addMetadata("request", {
							url: config.url,
							method: config.method,
							headers: config.headers,
							body: this.sanitizeRequestBody(requestData)
						});
						event.addMetadata("response", {
							status: this.error.response.status,
							data: this.error.response.data
						});
					});

				}
				pwaStore.requireSync = true;
				const element = document.getElementById("modal-sync-error");
				if (element) {
					const modal = new Modal(element, {});
					modal.show();
				}
			}
			// regardless of success, gets the current user from the db - this
			// must be awaited (not fire-and-forget) so that updateInProgress
			// stays true, and syncCurrentUser() itself doesn't resolve, until
			// the local cache has actually been refreshed. Otherwise a second
			// syncCurrentUser() call (e.g. from the 1s polling interval) can
			// run before this refresh lands, re-submitting the same
			// pre-refresh (stale/null synced_at) records that this request
			// just got written to the DB, and have them rejected.
			pwaStore.requireSync = false;
			try {
				await this.getCurrentUser();
				pwaStore.endUpdate(this.user?.id ?? null, true);
			} catch {
				pwaStore.endUpdate(null);
			}

		},
		sanitizeRequestBody (body: any) {
			if (!body) return body;
			const cloned = { ...body };
			delete cloned.password;
			delete cloned.password_confirmation;
			return cloned;
		},
		logout () {
			return new Promise<void>((resolve, reject) => {
				const alertStore = useAlertStore();

				// assume we have been through the UI part of the process,
				// ensure that the app is left in an online state,
				// otherwise we may not be able to log back in
				const pwaStore = usePwaStore();

				alertStore.clearAll();
				const authService = new AuthService();

				const globalStateStore = useGlobalStateStore();

				// Put all the logic here to complete logging out in the frontend
				const completeLogout = () => {
					// this.clearBrowserData();
					this.user = null;
					// reset vars so we dont ask again
					if (router.currentRoute.value.name !== "login") {
						router.push({ path: "/login" });
						console.log("logout...");
					}
				};

				return authService
					.logout()
					.then(() => {
						completeLogout();
						resolve();
						pwaStore.setManuallyOnline(true);
					})
					.catch((err) => {
						// If logout failed with a 419 or a 401 then the session
						// has already expired on the server, so just complete
						// logging out as above.
						// (We should not get here with 419 because logout route
						// is excluded from the VerifyCsrfMiddleware.php)
						const status: number | null = err?.response?.status;
						if (status !== null && [401, 419].includes(status)) {
							console.log("got " + status);
							completeLogout();
							resolve();
						} else {
							alertStore.error(getResponseError(err));
							reject(err);
						}
					});
			});
		},
		hasAbilities (abilities: any) {
			return Object.prototype.hasOwnProperty.call(this.user, "abilities") && !!this.user.abilities.find((ab: any) => {
				if (ab.name === "*") {
					return true;
				}
				if (typeof abilities === "string") {
					return ab.name === abilities;
				}
				return !!abilities.find((p: any) => {
					return ab.name === p;
				});
			});
		},

		hasAllAbilities (abilities: any) {
			let isAvailable = true;
			if (Object.prototype.hasOwnProperty.call(this.user, "abilities")) {
				this.user.abilities.filter((ab: any) => {
					const hasContain = !!abilities.find((p: any) => {
						return ab.name === p;
					});
					if (!hasContain) {
						isAvailable = false;
					}
				});
			}

			return isAvailable;
		}
	},
	getters: {
		isAdmin: (state: AuthStore) => {
			return state.user ? state.user.is_admin : false;
		},
		loggedIn: (state: AuthStore) => {
			return !!state.user;
		},
		guest: (state: AuthStore) => {
			return state.user == null;
		}
	},
	persist: true
});
